/**
 * What leaving a record, a list or a field out takes with it, and what it
 * leaves changed (board `canvas-direct-leave` 5.19: "Goes with it" / "Stays:
 * Staff — Payments received still picks from it"), and the one cascade the
 * reducer applies with `cascade: 'with_dependents'` (J2.11).
 *
 *   Leaving out a field F of record R
 *     goes    formula fields that read F (a field, a hop that lands on F or
 *             goes through it, a total over it); R's cards and Dashboard
 *             cards that read, filter or group by it (when F is R's measure,
 *             a card that names no field and does not count, and a Dashboard card's
 *             leaf that totals the measure, read it); link conditions and
 *             fills that read it (an emptied group goes, the link stays)
 *     stays   bindings naming F are unset and the mend picks the headline
 *             again; the layout drops it, seed rows drop its cell, a sheet
 *             that names it goes (`reads`); other fields' filters on it go
 *             (`picks_from`), their starting values and show rules reading it
 *             go (`reads`); import columns into it are left out
 *             (`imports_into`); notes about it go (`about`)
 *   Leaving out a record or list R
 *     goes    every field anywhere that picks from R, with its own cascade;
 *             links from or to R; Dashboard cards with a total on R; imports
 *             into R; notes about R
 *     stays   role access rows naming R are dropped, the role stays (`grants`)
 *
 * The cascade runs to a fixed point, so a field that goes takes its own
 * dependents. The target and what sits under it (a record's fields and cards,
 * a lines field's line fields, notes about the target) are not dependents:
 * they go with it by definition.
 */
import type { SetupDraftField, SetupDraftFormulaNode } from '../compose-author';
import { studioMeasureOf } from './cards';
import type { StudioCardNode, StudioDraft, StudioRecord } from './draft';
import { studioPath, studioRefOf } from './paths';

export type StudioDependents = {
  goes: string[];
  stays: Array<{ path: string; because: 'picks_from' | 'reads' | 'posts_into' | 'shows' | 'imports_into' | 'grants' | 'about' }>;
};

type Because = StudioDependents['stays'][number]['because'];

const fieldPath = (record: string, within: readonly string[], field: string): string =>
  studioPath({ kind: 'field', record, ...(within.length === 0 ? {} : { within: [...within] }), field });

/** Every field of a record at every depth, with the list it sits in. */
const everyField = (record: StudioRecord): Array<{ within: string[]; field: SetupDraftField; siblings: SetupDraftField[] }> => {
  const out: Array<{ within: string[]; field: SetupDraftField; siblings: SetupDraftField[] }> = [];
  const walk = (fields: SetupDraftField[], within: string[]): void => {
    for (const field of fields) {
      out.push({ within, field, siblings: fields });
      if (field.lines !== undefined) walk(field.lines.fields, [...within, field.key]);
    }
  };
  walk(record.fields, []);
  return out;
};

/** The paths a formula tree reads, standing in list `siblings` of record `record` at `within`. */
const formulaReads = (
  node: SetupDraftFormulaNode,
  record: string,
  within: readonly string[],
  siblings: readonly SetupDraftField[],
): string[] => {
  const out: string[] = [];
  const listOf = (key: string): string | undefined => siblings.find((one) => one.key === key)?.targetRecordKey;
  const walk = (one: SetupDraftFormulaNode): void => {
    switch (one.kind) {
      case 'field':
        out.push(fieldPath(record, within, one.fieldKey));
        return;
      case 'hop': {
        out.push(fieldPath(record, within, one.via));
        const list = listOf(one.via);
        if (list !== undefined) out.push(fieldPath(list, [], one.fieldKey));
        return;
      }
      case 'agg': {
        out.push(fieldPath(record, within, one.lines));
        if (one.of === undefined) return;
        const held = siblings.find((field) => field.key === one.lines);
        if (held?.type === 'lines') out.push(fieldPath(record, [...within, one.lines], one.of));
        else if (held?.targetRecordKey !== undefined) out.push(fieldPath(held.targetRecordKey, [], one.of));
        return;
      }
      case 'const':
        return;
      case 'calc':
        walk(one.left);
        walk(one.right);
        return;
      case 'round':
        walk(one.of);
        return;
      case 'if':
        walk(one.left);
        if (one.right !== undefined) walk(one.right);
        walk(one.then);
        walk(one.otherwise);
        return;
      case 'days_between':
        walk(one.from);
        walk(one.to);
        return;
      case 'concat':
        one.parts.forEach(walk);
        return;
      case 'coalesce':
        one.of.forEach(walk);
        return;
    }
  };
  walk(node);
  return out;
};

/** The paths one Dashboard card's leaves read: their records, and every field (a leaf totalling the measure reads the measure). */
const cardReads = (draft: StudioDraft, node: StudioCardNode, measures: ReadonlyMap<string, string | undefined>): { records: string[]; fields: string[] } => {
  const records: string[] = [];
  const fields: string[] = [];
  const walk = (one: StudioCardNode): void => {
    if (one.kind === 'const') return;
    if (one.kind === 'calc') {
      walk(one.left);
      walk(one.right);
      return;
    }
    if (one.kind === 'round') {
      walk(one.of);
      return;
    }
    records.push(one.record);
    const record = draft.records.find((held) => held.key === one.record);
    const listOf = (key: string): string | undefined => record?.fields.find((field) => field.key === key)?.targetRecordKey;
    if (one.of?.kind === 'field') fields.push(fieldPath(one.record, [], one.of.field));
    const measure = measures.get(one.record);
    if (one.of?.kind === 'measure' && measure !== undefined) fields.push(fieldPath(one.record, [], measure));
    if (one.of?.kind === 'expr') fields.push(...formulaReads(one.of.formula.expr, one.record, [], record?.fields ?? []));
    for (const where of one.where ?? []) {
      if (where.via === undefined) fields.push(fieldPath(one.record, [], where.field));
      else {
        fields.push(fieldPath(one.record, [], where.via));
        const list = listOf(where.via);
        if (list !== undefined) fields.push(fieldPath(list, [], where.field));
      }
    }
    if (one.groupBy !== undefined) fields.push(fieldPath(one.record, [], one.groupBy));
  };
  walk(node);
  return { records, fields };
};

/** What is gone: records by key, fields by path. A field is gone when it, a lines field above it, or its record is. */
type Gone = { records: Set<string>; fields: Set<string> };

const isGone = (gone: Gone, path: string): boolean => {
  const ref = studioRefOf(path);
  if (ref === null) return false;
  if (ref.kind === 'record') return gone.records.has(ref.record);
  if (ref.kind !== 'field') return false;
  if (gone.records.has(ref.record)) return true;
  const chain = [...(ref.within ?? []), ref.field];
  for (let depth = 1; depth <= chain.length; depth += 1) {
    if (gone.fields.has(fieldPath(ref.record, chain.slice(0, depth - 1), chain[depth - 1]!))) return true;
  }
  return false;
};

/** The fixed point: every field that goes because something it reads or picks from went. */
const closeOver = (draft: StudioDraft, gone: Gone): void => {
  for (let changed = true; changed; ) {
    changed = false;
    for (const record of draft.records) {
      if (gone.records.has(record.key)) continue;
      for (const { within, field, siblings } of everyField(record)) {
        const path = fieldPath(record.key, within, field.key);
        if (isGone(gone, path)) continue;
        const picks = (field.type === 'reference' || field.type === 'multi_select') && field.targetRecordKey !== undefined && gone.records.has(field.targetRecordKey);
        const reads =
          field.type === 'formula' &&
          field.formula !== undefined &&
          formulaReads(field.formula.expr, record.key, within, siblings).some((read) => read !== path && isGone(gone, read));
        if (picks || reads) {
          gone.fields.add(path);
          changed = true;
        }
      }
    }
  }
};

/** Whether `path` is the target or sits under it. */
const under = (path: string, target: string): boolean => path === target || path.startsWith(`${target}/`);

/**
 * The scrub: every reference to what is gone, outside what goes itself.
 * Reports what goes and what stays changed; with `mutate`, also makes the
 * change on `draft` (which must be the caller's own copy).
 */
const scrub = (draft: StudioDraft, gone: Gone, target: string, mutate: boolean): StudioDependents => {
  const goes: string[] = [];
  const stays: StudioDependents['stays'] = [];
  const stay = (path: string, because: Because): void => {
    if (under(path, target)) return;
    if (!stays.some((one) => one.path === path && one.because === because)) stays.push({ path, because });
  };
  const go = (path: string): void => {
    if (!under(path, target) && !goes.includes(path)) goes.push(path);
  };
  const gonePath = (path: string): boolean => isGone(gone, path);
  // Each record's measure as it stands before anything is unset: a card that
  // names no field, and a Dashboard card's leaf of kind `measure`, read it.
  const measures = new Map(draft.records.map((record) => [record.key, studioMeasureOf(record)]));

  // Fields that go, beside the target.
  for (const path of gone.fields) go(path);

  for (const record of draft.records) {
    const rk = record.key;
    // Only the target record ever goes, and it is no dependent of its own.
    if (gone.records.has(rk)) continue;
    const top = (key: string | undefined): boolean => key !== undefined && gonePath(fieldPath(rk, [], key));

    // The record's own cards.
    const tiles = record.tiles ?? [];
    const keptTiles = tiles.filter((tile) => {
      // A count with no field counts rows (the headline at worst); any other agg with none totals the measure.
      const value = tile.fieldKey ?? (tile.agg === 'count' ? undefined : measures.get(rk));
      const reads = [value, tile.groupByFieldKey, ...(tile.where ?? []).map((one) => one.fieldKey)];
      if (!reads.some(top)) return true;
      go(`record:${rk}/card:${tile.key}`);
      return false;
    });
    if (mutate && keptTiles.length !== tiles.length) record.tiles = keptTiles;

    // Bindings, the sheet and the layout: the record's props.
    const props = `record:${rk}/props`;
    const bindings = ['headlineFieldKey', 'dateFieldKey', 'measureFieldKey', 'signFieldKey', 'focusFieldKey'] as const;
    for (const binding of bindings) {
      if (!top(record[binding])) continue;
      stay(props, 'reads');
      if (!mutate) continue;
      if (binding === 'headlineFieldKey') {
        record.headlineFieldKey = record.fields.find((field) => !gonePath(fieldPath(rk, [], field.key)))?.key ?? '';
      } else {
        delete record[binding];
      }
    }
    const capture = record.capture;
    if (capture !== undefined && [capture.overFieldKey, capture.cellFieldKey, ...(capture.sharedFieldKeys ?? [])].some(top)) {
      stay(props, 'reads');
      if (mutate) delete record.capture;
    }
    if (record.layout !== undefined) {
      const named = record.layout.flatMap((section) => [...section.fieldKeys, ...(section.sections ?? []).flatMap((inner) => inner.fieldKeys)]);
      if (named.some(top)) {
        stay(props, 'reads');
        if (mutate) {
          record.layout = record.layout.map((section) => ({
            ...section,
            fieldKeys: section.fieldKeys.filter((key) => !top(key)),
            ...(section.sections === undefined
              ? {}
              : { sections: section.sections.map((inner) => ({ ...inner, fieldKeys: inner.fieldKeys.filter((key) => !top(key)) })) }),
          }));
        }
      }
    }
    if ((record.seedRows ?? []).some((row) => row.values.some((cell) => top(cell.fieldKey)))) {
      stay(`record:${rk}/rows`, 'reads');
      if (mutate) record.seedRows = record.seedRows!.map((row) => ({ values: row.values.filter((cell) => !top(cell.fieldKey)) }));
    }

    // Every field that stays: its filter, its starting value, its show rule.
    for (const { within, field, siblings } of everyField(record)) {
      const path = fieldPath(rk, within, field.key);
      if (gonePath(path)) continue;
      if (field.filter !== undefined) {
        const reads = [
          ...(field.targetRecordKey === undefined ? [] : [fieldPath(field.targetRecordKey, [], field.filter.field)]),
          fieldPath(rk, within, field.filter.equals),
        ];
        if (reads.some(gonePath)) {
          stay(path, 'picks_from');
          if (mutate) delete field.filter;
        }
      }
      for (const member of ['startsWith', 'showWhen'] as const) {
        const formula = field[member];
        if (formula === undefined) continue;
        if (formulaReads(formula.expr, rk, within, siblings).some(gonePath)) {
          stay(path, 'reads');
          if (mutate) delete field[member];
        }
      }
    }
  }

  // Dashboard cards.
  const keptBoards = draft.boardCards.filter((card) => {
    const reads = cardReads(draft, card.total, measures);
    if (!reads.records.some((key) => gone.records.has(key)) && !reads.fields.some(gonePath)) return true;
    go(`board:${card.key}`);
    return false;
  });
  if (mutate) draft.boardCards = keptBoards;

  // Links.
  const keptLinks = draft.links.filter((link) => {
    if (gone.records.has(link.from) || gone.records.has(link.to)) {
      go(`link:${link.key}`);
      return false;
    }
    const from = draft.records.find((record) => record.key === link.from);
    const listOf = (key: string): string | undefined => from?.fields.find((field) => field.key === key)?.targetRecordKey;
    const throughGone = (via: string, field: string): boolean => {
      if (gonePath(fieldPath(link.from, [], via))) return true;
      const list = listOf(via);
      return list !== undefined && gonePath(fieldPath(list, [], field));
    };
    const conditionGone = (condition: (typeof link.when)[number]['any'][number]): boolean =>
      condition.via === undefined ? gonePath(fieldPath(link.from, [], condition.field)) : throughGone(condition.via, condition.field);
    const fillGone = (fill: (typeof link.fills)[number]): boolean => {
      if (gonePath(fieldPath(link.to, [], fill.to))) return true;
      switch (fill.kind) {
        case 'field':
          return gonePath(fieldPath(link.from, [], fill.field));
        case 'const':
          return false;
        case 'reference':
          return throughGone(fill.via, fill.field);
        case 'formula':
          return formulaReads(fill.formula.expr, link.from, [], from?.fields ?? []).some(gonePath);
      }
    };
    const when = link.when
      .map((group) => ({ any: group.any.filter((condition) => !conditionGone(condition)) }))
      .filter((group, index) => group.any.length > 0 || link.when[index]!.any.length === 0);
    const fills = link.fills.filter((fill) => !fillGone(fill));
    const changed = fills.length !== link.fills.length || JSON.stringify(when) !== JSON.stringify(link.when);
    if (changed) {
      stay(`link:${link.key}`, 'reads');
      if (mutate) {
        link.when = when;
        link.fills = fills;
      }
    }
    return true;
  });
  if (mutate) draft.links = keptLinks;

  // Imports.
  const keptImports = draft.imports.filter((imp) => {
    if (gone.records.has(imp.record)) {
      go(`import:${imp.key}`);
      return false;
    }
    if (imp.columns.some((column) => column.field !== null && gonePath(fieldPath(imp.record, [], column.field)))) {
      stay(`import:${imp.key}`, 'imports_into');
      if (mutate) {
        imp.columns = imp.columns.map((column) =>
          column.field !== null && gonePath(fieldPath(imp.record, [], column.field)) ? { ...column, field: null } : column,
        );
      }
    }
    return true;
  });
  if (mutate) draft.imports = keptImports;

  // Roles.
  for (const role of draft.roles) {
    if (!role.access.some((row) => gone.records.has(row.record))) continue;
    stay(`role:${role.key}`, 'grants');
    if (mutate) role.access = role.access.filter((row) => !gone.records.has(row.record));
  }

  // Notes and provenance about what goes.
  const aboutGone = (path: string): boolean => {
    const ref = studioRefOf(path);
    if (ref === null || ref.kind === 'workspace' || !('record' in ref)) return false;
    if (gone.records.has(ref.record)) return true;
    return ref.kind === 'field' && gonePath(path);
  };
  const keptNotes = draft.notes.filter((note) => {
    if (!aboutGone(note.about)) return true;
    const ref = studioRefOf(note.about);
    if (ref !== null && 'record' in ref && gone.records.has(ref.record)) go(`note:${note.key}`);
    else stay(`note:${note.key}`, 'about');
    return false;
  });
  if (mutate) {
    draft.notes = keptNotes;
    draft.from = draft.from.filter((entry) => !aboutGone(entry.path));
  }

  // Last, the gone records and fields themselves.
  if (mutate) {
    draft.records = draft.records.filter((record) => !gone.records.has(record.key));
    for (const record of draft.records) {
      const prune = (fields: SetupDraftField[], within: string[]): SetupDraftField[] =>
        fields
          .filter((field) => !gone.fields.has(fieldPath(record.key, within, field.key)))
          .map((field) =>
            field.lines === undefined ? field : { ...field, lines: { ...field.lines, fields: prune(field.lines.fields, [...within, field.key]) } },
          );
      record.fields = prune(record.fields, []);
    }
  }
  return { goes, stays };
};

const goneOf = (draft: StudioDraft, path: string): Gone | null => {
  const ref = studioRefOf(path);
  const gone: Gone = { records: new Set(), fields: new Set() };
  if (ref?.kind === 'record') gone.records.add(ref.record);
  else if (ref?.kind === 'field') gone.fields.add(path);
  else return null;
  closeOver(draft, gone);
  return gone;
};

/** What leaving the record, list or field at `path` out takes with it, and what it leaves changed. */
export const studioDependents = (draft: StudioDraft, path: string): StudioDependents => {
  const gone = goneOf(draft, path);
  if (gone === null) return { goes: [], stays: [] };
  return scrub(structuredClone(draft), gone, path, false);
};

/** True when leaving `path` out touches nothing beside it. */
export const studioHasDependents = (dependents: StudioDependents): boolean => dependents.goes.length > 0 || dependents.stays.length > 0;

/**
 * Leaves the part at `path` out of `draft` (the caller's own copy), with every
 * dependent the table names, and answers what it changed. A record or field
 * that is not there changes nothing.
 */
export const removeWithDependents = (draft: StudioDraft, path: string): StudioDependents => {
  const gone = goneOf(draft, path);
  if (gone === null) return { goes: [], stays: [] };
  return scrub(draft, gone, path, true);
};
